import React from "react";
import { usePageMetadata } from "../../Core/Content/Hooks";
import { useEffect, useRef, useState } from "react";
import AppService from "../../service/app.service";

export const useHomeHook = () => {
  const [homePageData, setHomePageData] = useState(null);
  usePageMetadata(homePageData, "SouledOut | Home");
  const appService = new AppService();
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchedRef = useRef(false);
  useEffect(() => {
    if (fetchedRef.current) return; // guard against React StrictMode double-invoke
    fetchedRef.current = true;
    (async () => {
      try {
        const result = await appService.getPage("home");
        setHomePageData(result.data);
        setError(result.error);
        setIsLoading(result.isLoading);
      } catch (e: any) {
        setError(e);
        setIsLoading(false);
      }
    })();
  }, []);

  return {
    homePageData,
    error,
    isLoading,
  };
};
export function useHomePresentation(homePageData: any) {
  const [featureSetIndex, setFeatureSetIndex] = useState(0); // which top-level featured set (0 or 1)
  const [categoryIndex, setCategoryIndex] = useState(0); // which category inside the set (start centered)
  const [phase, setPhase] = useState<"idle" | "exiting" | "entering">("idle");
  const pendingNavRef = useRef<null | { dir: "left" | "right" }>(null);

  const featuredSets = homePageData?.featured || [];
  const categories = featuredSets[featureSetIndex] || [];
  const activeCategory = categories[categoryIndex];
  const rawFeaturedDrinks = homePageData?.featuredDrinks || [];
  // Drinks data may mirror featured: an array whose first element may itself be an array of drink category objects
  // Normalize to a categories array: [{ title, items: [...]}, ...]
  const drinkCategories = React.useMemo(() => {
    if (!Array.isArray(rawFeaturedDrinks)) return [];
    if (rawFeaturedDrinks.length === 0) return [];
    // Case 1: [[{title, items}, {title, items}]]
    if (Array.isArray(rawFeaturedDrinks[0])) {
      return rawFeaturedDrinks[0] as any[];
    }
    // Case 2: already array of category objects (have 'items')
    if (
      rawFeaturedDrinks.every(
        (el: any) => el && typeof el === "object" && "items" in el
      )
    ) {
      return rawFeaturedDrinks as any[];
    }
    // Case 3: raw items array -> wrap into single pseudo-category
    return [{ title: "Drinks", items: rawFeaturedDrinks }];
  }, [rawFeaturedDrinks]);
  const [drinkCategoryIndex, setDrinkCategoryIndex] = useState(0);
  const [drinkPhase, setDrinkPhase] = useState<"idle" | "exiting" | "entering">(
    "idle"
  );
  const pendingDrinkNavRef = useRef<null | { dir: "left" | "right" }>(null);
  const activeDrinkCategory = drinkCategories[drinkCategoryIndex];

  // Navigation handler coming from header (left/right) for food
  const handleNavigate = (direction: "left" | "right") => {
    if (!categories.length || phase !== "idle") return; // ignore during transition
    pendingNavRef.current = { dir: direction };
    setPhase("exiting");

    // after exit animation, update indices then start enter
    setTimeout(() => {
      if (!pendingNavRef.current) return;
      const dir = pendingNavRef.current.dir;
      let nextFeatureSet = featureSetIndex;
      let nextCategory = categoryIndex;

      if (dir === "right") {
        if (categoryIndex < categories.length - 1) {
          nextCategory = categoryIndex + 1;
        } else {
          nextFeatureSet =
            featureSetIndex === featuredSets.length - 1
              ? 0
              : featureSetIndex + 1;
          const middle =
            (featuredSets[nextFeatureSet]?.length ?? 0) > 2 ? 1 : 0;
          nextCategory = middle;
        }
      } else {
        if (categoryIndex > 0) {
          nextCategory = categoryIndex - 1;
        } else {
          nextFeatureSet =
            featureSetIndex === 0
              ? featuredSets.length - 1
              : featureSetIndex - 1;
          const prevCategories = featuredSets[nextFeatureSet] || [];
          nextCategory =
            prevCategories.length > 0 ? prevCategories.length - 1 : 0;
        }
      }

      setFeatureSetIndex(nextFeatureSet);
      setCategoryIndex(nextCategory);
      setPhase("entering");

      setTimeout(() => {
        setPhase("idle");
        pendingNavRef.current = null;
      }, 460); // enter duration (match CSS ~450ms)
    }, 260); // exit duration (match CSS 250ms)
  };

  // Navigation for drinks
  const handleDrinkNavigate = (direction: "left" | "right") => {
    if (!drinkCategories.length || drinkPhase !== "idle") return;
    pendingDrinkNavRef.current = { dir: direction };
    setDrinkPhase("exiting");
    setTimeout(() => {
      if (!pendingDrinkNavRef.current) return;
      const dir = pendingDrinkNavRef.current.dir;
      let nextIndex = drinkCategoryIndex;
      if (dir === "right") {
        nextIndex =
          drinkCategoryIndex < drinkCategories.length - 1
            ? drinkCategoryIndex + 1
            : 0;
      } else {
        nextIndex =
          drinkCategoryIndex > 0
            ? drinkCategoryIndex - 1
            : drinkCategories.length - 1;
      }
      setDrinkCategoryIndex(nextIndex);
      setDrinkPhase("entering");
      setTimeout(() => {
        setDrinkPhase("idle");
        pendingDrinkNavRef.current = null;
      }, 460);
    }, 260);
  };

  return {
    featureSetIndex,
    categoryIndex,
    phase,
    categories,
    activeCategory,
    drinkCategories,
    drinkCategoryIndex,
    drinkPhase,
    activeDrinkCategory,
    handleNavigate,
    handleDrinkNavigate,
  };
}
